import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.js';
import { Hint } from '@synoptic/ui-kit/hint/hint.tsx';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.tsx';
import {
  MultiTag,
  MultiTagItem,
} from '@synoptic/ui-kit/multi-tag/multi-tag.tsx';
import { Wrap } from '@synoptic/ui-kit/wrap.js';
import { ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { CustomTargetsDataFragmentFragment } from '@/__generated__/graphql.ts';
import { StreamSubscription } from '@/__generated__/schema-types.ts';
import {
  actionsContainer,
  addedTargets as addedTargetsStyle,
  listRow,
  listRowDeck,
  listRowName,
  listRowText,
  lists,
  listsHeader,
  modalDescription,
  targetsBottomTip,
  targetTag,
} from '@/components/streams/custom-stream/targets-modal.css.ts';
import {
  CustomCollectionRef,
  useAddTargetsToCustomCollections,
} from '@/components/streams/custom-stream/use-add-targets-to-custom-collections.ts';
import { useSetCustomTargets } from '@/components/streams/custom-stream/use-set-custom-targets.ts';
import { useUpdateStreamSubscription } from '@/components/streams/custom-stream/use-update-stream-subscription.ts';
import { PriceStepperSection } from '@/components/streams/payment-options/price-stepper-section.tsx';
import { useStreamDecks } from '@/routes/_app/use-stream-decks.ts';
import {
  getInitialTargetsCount,
  getNextValidTargetsCount,
} from '../payment-options/price-stepper-utils';

type SubscriptionPricing = Pick<
  StreamSubscription,
  'price' | 'interval' | 'intervalCount' | 'customTargets' | 'cancelAt'
>;

type View = 'targets' | 'updateQuantity' | 'addToLists';

type SubsetList = CustomCollectionRef & { name: string; deckName: string };

const useSubsetLists = (streamId: string, excludeCollectionId?: string) => {
  const streamDecks = useStreamDecks();
  const showDeckName = streamDecks.length > 1;

  return streamDecks.flatMap((deck) =>
    deck.collections
      .filter(
        (collection) =>
          collection.id !== excludeCollectionId &&
          collection.sourceConfigs.some(
            (config) => config.stream.id === streamId && !config.usesAllTargets,
          ),
      )
      .map((collection) => ({
        id: collection.id,
        streamDeckId: deck.id,
        name: collection.name,
        deckName: showDeckName ? deck.name : '',
      })),
  );
};

export const TargetsModal = ({
  trigger,
  streamId,
  maxTargets,
  initialTargets,
  instruction,
  targetValidationRegex,
  refetchStreamPosts,
  customTargetsData,
  subscription,
  excludeCollectionId,
  open: controlledOpen,
  onOpenChange: onControlledOpenChange,
}: {
  trigger?: ReactNode;
  streamId?: string;
  maxTargets: number;
  initialTargets?: string[];
  instruction?: string;
  targetValidationRegex?: string | null;
  refetchStreamPosts?: boolean;
  customTargetsData?: CustomTargetsDataFragmentFragment | null;
  subscription?: SubscriptionPricing | null;
  excludeCollectionId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const routeStreamId = useParams().id as string;
  const id = streamId ?? routeStreamId;
  const [internalOpen, setInternalOpen] = useState(false);
  const [view, setView] = useState<View>('targets');
  const [addedTargets, setAddedTargets] = useState<string[]>([]);
  const subsetLists = useSubsetLists(id, excludeCollectionId);

  const open = controlledOpen ?? internalOpen;
  const onOpenChange = (next: boolean) => {
    setInternalOpen(next);
    onControlledOpenChange?.(next);
    if (!next) setView('targets');
  };

  const onTargetsSubmitted = (nextTargets: string[]) => {
    const previous = new Set(initialTargets ?? []);
    const added = nextTargets.filter((target) => !previous.has(target));
    if (added.length === 0 || subsetLists.length === 0) {
      onOpenChange(false);
      return;
    }
    setAddedTargets(added);
    setView('addToLists');
  };

  const canAddMoreTargets = !!customTargetsData && !!subscription;
  const isAtMaxLimit =
    canAddMoreTargets &&
    getNextValidTargetsCount(
      subscription.customTargets ?? 0,
      customTargetsData,
    ) > customTargetsData.maxTargets;
  const isSubscriptionCancelled = !!subscription?.cancelAt;

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange}>
      {trigger ? <ModalTrigger asChild>{trigger}</ModalTrigger> : null}
      <ModalContent>
        {view === 'targets' ? (
          <EditTargetsView
            streamId={id}
            maxTargets={maxTargets}
            initialTargets={initialTargets}
            instruction={instruction}
            targetValidationRegex={targetValidationRegex}
            refetchStreamPosts={refetchStreamPosts}
            onClose={() => onOpenChange(false)}
            onSubmitted={onTargetsSubmitted}
            onAddMoreTargets={
              canAddMoreTargets ? () => setView('updateQuantity') : undefined
            }
            isAtMaxLimit={isAtMaxLimit}
            isSubscriptionCancelled={isSubscriptionCancelled}
          />
        ) : view === 'addToLists' ? (
          <AddToListsView
            streamId={id}
            addedTargets={addedTargets}
            lists={subsetLists}
            onDone={() => onOpenChange(false)}
          />
        ) : (
          <UpdateQuantityView
            streamId={id}
            customTargetsData={customTargetsData!}
            subscription={subscription!}
            onBack={() => setView('targets')}
            onSuccess={() => onOpenChange(false)}
          />
        )}
      </ModalContent>
    </ModalRoot>
  );
};

const EditTargetsView = ({
  streamId,
  maxTargets,
  initialTargets,
  instruction,
  targetValidationRegex,
  refetchStreamPosts,
  onClose,
  onSubmitted,
  onAddMoreTargets,
  isAtMaxLimit,
  isSubscriptionCancelled,
}: {
  streamId: string;
  maxTargets: number;
  initialTargets?: string[];
  instruction?: string;
  targetValidationRegex?: string | null;
  refetchStreamPosts?: boolean;
  onClose: () => void;
  onSubmitted: (targets: string[]) => void;
  onAddMoreTargets?: () => void;
  isAtMaxLimit?: boolean;
  isSubscriptionCancelled?: boolean;
}) => {
  const [targets, setTargets] = useState<string[]>(initialTargets || []);

  const [setCustomTargets, { loading }] = useSetCustomTargets(
    streamId,
    refetchStreamPosts,
  );

  const onSubmit = async () => {
    const { data } = await setCustomTargets(targets);
    if (data?.setStreamCustomTargets) {
      onSubmitted(data.setStreamCustomTargets);
    } else {
      onClose();
    }
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Submit your targets</ModalTitle>
      </ModalHeader>
      <p className={modalDescription}>
        Add the targets you want this stream to monitor. Follow the author’s
        instruction and separate targets with a whitespace or comma in between —
        we’ll sort it out.
      </p>
      {instruction ? (
        <p className={modalDescription}>
          <strong>Author&apos;s instruction: </strong>
          {instruction}
        </p>
      ) : null}
      {targetValidationRegex ? (
        <p className={modalDescription}>
          <strong>Regex: </strong>
          {targetValidationRegex}
        </p>
      ) : null}

      <MultiTag
        value={targets}
        onChange={setTargets}
        label={'Select targets you want to monitor:'}
        placeholder={'Type or paste targets here...'}
        maxItems={maxTargets}
        bottomTip={
          <div className={targetsBottomTip}>
            <span>
              {targets.length} / {maxTargets} target
              {targets.length !== 1 ? 's' : ''} added
            </span>{' '}
            {onAddMoreTargets ? (
              <Wrap
                with={Hint}
                if={isAtMaxLimit || isSubscriptionCancelled}
                description={
                  isSubscriptionCancelled
                    ? 'Your subscription is cancelled. Renew it to add more targets.'
                    : 'You already have the maximum targets limit allowed by the author.'
                }
              >
                <LinkButton
                  size="medium"
                  onClick={onAddMoreTargets}
                  disabled={isAtMaxLimit || isSubscriptionCancelled}
                >
                  Buy more targets
                </LinkButton>
              </Wrap>
            ) : null}
          </div>
        }
        regex={targetValidationRegex ?? undefined}
        TagItem={({ children, onRemove }) => (
          <MultiTagItem onRemove={onRemove} className={targetTag}>
            {children}
          </MultiTagItem>
        )}
      />

      <div className={actionsContainer}>
        <Button variant={'secondary'} onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={targets.length <= 0 || targets.length > maxTargets}
          loading={loading}
        >
          Submit targets
        </Button>
      </div>
    </>
  );
};

const AddToListsView = ({
  streamId,
  addedTargets,
  lists: subsetLists,
  onDone,
}: {
  streamId: string;
  addedTargets: string[];
  lists: SubsetList[];
  onDone: () => void;
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(subsetLists.map((list) => list.id)),
  );
  const [addTargetsToCustomCollections, { loading }] =
    useAddTargetsToCustomCollections(streamId);

  const allSelected = selectedIds.size === subsetLists.length;

  const toggleList = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onSubmit = async () => {
    const selected = subsetLists
      .filter((list) => selectedIds.has(list.id))
      .map(({ id, streamDeckId }) => ({ id, streamDeckId }));
    const { data } = await addTargetsToCustomCollections(
      addedTargets,
      selected,
    );
    if (data) {
      onDone();
    }
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Add new targets to your lists?</ModalTitle>
      </ModalHeader>
      <p className={modalDescription}>
        These lists include only some of this stream&apos;s targets, so new
        targets are left out of them until you add them. Choose the lists that
        should also receive:
      </p>
      <div className={addedTargetsStyle} data-testid="added-targets">
        {addedTargets.map((target) => (
          <Badge key={target} color="neutral">
            {target}
          </Badge>
        ))}
      </div>
      <div className={listsHeader}>
        <span>
          {selectedIds.size} of {subsetLists.length} selected
        </span>
        <Checkbox
          checked={
            allSelected ? true : selectedIds.size > 0 ? 'indeterminate' : false
          }
          aria-label="Toggle all lists"
          onCheckedChange={() =>
            setSelectedIds(
              allSelected
                ? new Set()
                : new Set(subsetLists.map((list) => list.id)),
            )
          }
        />
      </div>
      <div className={lists} data-testid="add-targets-lists">
        {subsetLists.map((list) => (
          <label key={list.id} className={listRow}>
            <Checkbox
              checked={selectedIds.has(list.id)}
              aria-label={`Toggle list ${list.name}`}
              onCheckedChange={() => toggleList(list.id)}
            />
            <span className={listRowText}>
              <span className={listRowName}>{list.name}</span>
              {list.deckName ? (
                <span className={listRowDeck}>{list.deckName}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
      <div className={actionsContainer}>
        <Button variant={'secondary'} onClick={onDone} disabled={loading}>
          Skip
        </Button>
        <Button
          onClick={onSubmit}
          disabled={selectedIds.size === 0}
          loading={loading}
        >
          Add to {selectedIds.size} {selectedIds.size === 1 ? 'list' : 'lists'}
        </Button>
      </div>
    </>
  );
};

const UpdateQuantityView = ({
  streamId,
  customTargetsData,
  subscription,
  onBack,
  onSuccess,
}: {
  streamId: string;
  customTargetsData: CustomTargetsDataFragmentFragment;
  subscription: SubscriptionPricing;
  onBack: () => void;
  onSuccess: () => void;
}) => {
  const initialTargetsCount =
    subscription.customTargets ?? getInitialTargetsCount(customTargetsData);
  const nextValidTargetsCount = getNextValidTargetsCount(
    initialTargetsCount,
    customTargetsData,
  );

  const [targetsCount, setTargetsCount] = useState(initialTargetsCount);

  const [updateSubscription, { loading }] =
    useUpdateStreamSubscription(streamId);

  const stepperData = {
    ...customTargetsData,
    minTargets: nextValidTargetsCount,
  };

  const handleSetTargetsCount = (next: number) => {
    if (next > targetsCount && next < nextValidTargetsCount) {
      setTargetsCount(nextValidTargetsCount);
      return;
    }
    setTargetsCount(next);
  };

  const totalSteps = targetsCount / customTargetsData.stepSize;
  const isBelowMinValid = targetsCount < nextValidTargetsCount;

  const onSubmit = async () => {
    await updateSubscription(totalSteps);
    onSuccess();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Add more targets</ModalTitle>
      </ModalHeader>
      <p className={modalDescription}>
        Increase how many targets your subscription monitors. Your billing will
        be updated accordingly.
      </p>

      <PriceStepperSection
        customTargets={stepperData}
        selectedPrice={{
          amount: subscription.price,
          interval: subscription.interval,
          intervalCount: subscription.intervalCount,
        }}
        targetsCount={targetsCount}
        setTargetsCount={handleSetTargetsCount}
      />

      <div className={actionsContainer}>
        <Button variant={'secondary'} onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button onClick={onSubmit} disabled={isBelowMinValid} loading={loading}>
          Update
        </Button>
      </div>
    </>
  );
};
